import { Locales } from 'intlayer';

export const SUPPORTED_LOCALES = [Locales.ENGLISH, Locales.UKRAINIAN] as const;

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = Locales.ENGLISH;

export const LOCALE_COOKIE_NAME = 'trypillian-locale';
export const LOCALE_PREFERENCE_MAX_AGE = 60 * 60 * 24 * 365;

const LANGUAGE_TO_LOCALE: Record<string, AppLocale> = {
  en: Locales.ENGLISH,
  uk: Locales.UKRAINIAN,
};

export function isAppLocale(locale: string | undefined): locale is AppLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale ?? '');
}

export function normalizeAppLocale(
  value: string | undefined
): AppLocale | undefined {
  return isAppLocale(value) ? value : undefined;
}

export function inferAppLocaleFromLanguage(
  value: string | undefined
): AppLocale | undefined {
  if (!value) {
    return undefined;
  }

  const languageCode = value.split(',')[0]?.trim().split('-')[0]?.split(';')[0]?.toLowerCase();
  if (!languageCode) {
    return undefined;
  }

  return LANGUAGE_TO_LOCALE[languageCode];
}

export function inferAppLocaleFromBrowserPreferences(
  languages: readonly string[] | undefined
): AppLocale | undefined {
  if (!languages?.length) {
    return undefined;
  }

  for (const language of languages) {
    const inferredLocale = inferAppLocaleFromLanguage(language);
    if (inferredLocale) {
      return inferredLocale;
    }
  }

  return undefined;
}

export function readLocalePreferenceFromCookie(
  cookieSource: string | undefined
): AppLocale | undefined {
  if (!cookieSource) {
    return undefined;
  }

  const parts = cookieSource.split(';').map((part) => part.trim());
  const match = parts.find((part) => part.startsWith(`${LOCALE_COOKIE_NAME}=`));
  if (!match) {
    return undefined;
  }

  const value = match.split('=').slice(1).join('=');
  return normalizeAppLocale(value ? decodeURIComponent(value) : undefined);
}

export function persistLocalePreference(locale: AppLocale) {
  if (typeof document !== 'undefined') {
    document.cookie = `${LOCALE_COOKIE_NAME}=${encodeURIComponent(locale)}; path=/; max-age=${LOCALE_PREFERENCE_MAX_AGE}; SameSite=Lax`;
  }
}
